/* Native scrolling, progressive enhancement, no animation dependencies. */
(() => {
  if (customElements.get("ts-luxe-menu")) return;
  class LuxeMenu extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const dialog = this.querySelector("dialog");
      const opener = this.querySelector("[data-menu-open]");
      const close = () => {
        dialog.close();
        opener.setAttribute("aria-expanded", "false");
        opener.focus();
      };
      opener.addEventListener(
        "click",
        () => {
          dialog.showModal();
          opener.setAttribute("aria-expanded", "true");
        },
        options,
      );
      this.querySelector("[data-menu-close]").addEventListener(
        "click",
        close,
        options,
      );
      dialog.addEventListener(
        "close",
        () => opener.setAttribute("aria-expanded", "false"),
        options,
      );
      dialog.addEventListener(
        "click",
        (event) => {
          if (event.target.closest("a")) close();
          if (event.target === dialog) {
            const rect = dialog.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right)
              close();
          }
        },
        options,
      );
    }
    disconnectedCallback() {
      this.controller?.abort();
    }
  }
  class LuxeScratch extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const stage = this.querySelector("[data-scratch-stage]");
      const canvas = stage.querySelector("canvas");
      const button = this.querySelector("[data-scratch-reveal]");
      const finishes = this.querySelectorAll("[data-scratch-finish]");
      this.dataset.finish =
        this.querySelector('[data-scratch-finish][aria-pressed="true"]')
          ?.dataset.scratchFinish ||
        this.dataset.finish ||
        "gold";
      const content = stage.querySelector("[data-scratch-announcement]");
      const artwork = this.querySelector("[data-card-artwork]");
      const transcript = this.querySelector("[data-card-transcript]");
      artwork.addEventListener(
        "load",
        () => this.removeAttribute("data-card-loading"),
        options,
      );
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) {
        this.setAttribute("data-unavailable", "");
        return;
      }
      let revealed = false,
        drawing = false,
        last = null;
      let pointer;
      const sampler = document.createElement("canvas");
      sampler.width = sampler.height = 64;
      const sampling = sampler.getContext("2d", { willReadFrequently: true });
      const stopDrawing = () => {
        const id = pointer;
        pointer = undefined;
        drawing = false;
        last = null;
        if (id !== undefined && canvas.hasPointerCapture(id)) canvas.releasePointerCapture(id);
      };
      const reveal = () => {
        revealed = true;
        stopDrawing();
        canvas.hidden = true;
        stage.setAttribute("data-active", "");
        content.removeAttribute("aria-hidden");
        content.setAttribute("role", "status");
        button.textContent = button.dataset.resetLabel;
        button.setAttribute("aria-expanded", "true");
        this.dispatchEvent(
          new CustomEvent("ts:scratch:revealed", { bubbles: true }),
        );
      };
      const paint = (preserve = false) => {
        if (revealed) return;
        const { width, height } = stage.getBoundingClientRect();
        if (!width || !height) return;
        const dpr = Math.min(devicePixelRatio || 1, 2);
        if (preserve && canvas.width === Math.round(width * dpr) && canvas.height === Math.round(height * dpr)) return;
        let previous;
        if (preserve && canvas.width && canvas.height) {
          previous = document.createElement("canvas");
          previous.width = canvas.width;
          previous.height = canvas.height;
          previous.getContext("2d").drawImage(canvas, 0, 0);
          stopDrawing();
        }
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        if (previous) {
          context.drawImage(previous, 0, 0, canvas.width, canvas.height);
          context.setTransform(dpr, 0, 0, dpr, 0, 0);
          return;
        }
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
        context.globalCompositeOperation = "source-over";
        const style = getComputedStyle(this);
        const foil = context.createLinearGradient(0, 0, width, height);
        const dark = style.getPropertyValue("--ts-foil-dark").trim();
        const light = style.getPropertyValue("--ts-foil-light").trim();
        const mid = style.getPropertyValue("--ts-foil").trim();
        foil.addColorStop(0, dark);
        foil.addColorStop(0.28, mid);
        foil.addColorStop(0.48, light);
        foil.addColorStop(0.67, mid);
        foil.addColorStop(1, dark);
        context.fillStyle = foil;
        context.fillRect(0, 0, width, height);
        // A deterministic grain keeps the foil tactile without loading a texture.
        for (let y = 0; y < height; y += 2) {
          for (let x = 0; x < width; x += 2) {
            const grain = (x * 17 + y * 31) % 13;
            context.fillStyle = grain < 6 ? "#fff1" : "#0001";
            context.fillRect(x, y, 1, 1);
          }
        }
        context.fillStyle = "#1b1a15";
        context.font = "11px Arial";
        context.textAlign = "center";
        context.fillText(
          stage.querySelector(".ts-luxe-scratch-cover span").textContent,
          width / 2,
          height / 2 - 4,
        );
        context.font = "10px Arial";
        context.fillText(
          stage.querySelector(".ts-luxe-scratch-cover small").textContent,
          width / 2,
          height / 2 + 17,
          width - 24,
        );
        stage.setAttribute("data-ready", "");
      };
      const reset = () => {
        revealed = false;
        stopDrawing();
        canvas.hidden = false;
        content.setAttribute("aria-hidden", "true");
        content.removeAttribute("role");
        stage.removeAttribute("data-active");
        button.setAttribute("aria-expanded", "false");
        button.textContent = button.dataset.showLabel;
        paint();
      };
      const point = (event) => {
        const rect = canvas.getBoundingClientRect();
        return { x: event.clientX - rect.left, y: event.clientY - rect.top };
      };
      const erase = (event) => {
        if (!drawing || revealed || event.pointerId !== pointer) return;
        const now = point(event);
        context.globalCompositeOperation = "destination-out";
        context.lineWidth = 42;
        context.lineCap = "round";
        context.beginPath();
        context.moveTo(last?.x ?? now.x, last?.y ?? now.y);
        context.lineTo(now.x, now.y);
        context.stroke();
        last = now;
      };
      canvas.addEventListener(
        "pointerdown",
        (event) => {
          if (revealed || drawing || !event.isPrimary || event.button !== 0) return;
          pointer = event.pointerId;
          drawing = true;
          last = point(event);
          canvas.setPointerCapture(event.pointerId);
          stage.setAttribute("data-active", "");
          erase(event);
        },
        options,
      );
      canvas.addEventListener("pointermove", erase, options);
      const finish = (event) => {
        if (event.pointerId !== pointer) return;
        stopDrawing();
        if (revealed) return;
        sampling.clearRect(0, 0, 64, 64);
        sampling.drawImage(canvas, 0, 0, 64, 64);
        const pixels = sampling.getImageData(0, 0, 64, 64).data;
        let clear = 0,
          total = 0;
        for (let i = 3; i < pixels.length; i += 4) {
          if (pixels[i] < 80) clear++;
          total++;
        }
        if (clear / total > 0.38) reveal();
      };
      canvas.addEventListener("pointerup", finish, options);
      canvas.addEventListener("pointercancel", finish, options);
      canvas.addEventListener("lostpointercapture", finish, options);
      button.addEventListener(
        "click",
        () => (revealed ? reset() : reveal()),
        options,
      );
      finishes.forEach((finish) =>
        finish.addEventListener(
          "click",
          () => {
            this.dataset.finish = finish.dataset.scratchFinish;
            if (
              artwork.src !==
              new URL(finish.dataset.cardSrc, location.href).href
            ) {
              this.setAttribute("data-card-loading", "");
              artwork.src = finish.dataset.cardSrc;
              if (artwork.complete && artwork.naturalWidth)
                this.removeAttribute("data-card-loading");
            }
            artwork.alt = `${finish.dataset.cardName} — ${artwork.dataset.altSuffix}`;
            transcript.textContent = finish.dataset.cardDescription;
            content.textContent = `${finish.dataset.cardName} — ${content.dataset.template}`;
            ["top", "left", "width", "height"].forEach((key) => {
              const value =
                finish.dataset[`card${key[0].toUpperCase()}${key.slice(1)}`];
              if (
                value !== undefined &&
                value !== "" &&
                Number.isFinite(Number(value))
              )
                this.style.setProperty(`--ts-scratch-${key}`, `${value}%`);
            });
            finishes.forEach((item) =>
              item.setAttribute("aria-pressed", String(item === finish)),
            );
            reset();
          },
          options,
        ),
      );
      this.resize = new ResizeObserver(() => paint(true));
      this.resize.observe(stage);
      reset();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
    }
  }
  class LuxeEntry extends HTMLElement {
    connectedCallback() {
      if (
        window.Shopify?.designMode ||
        !document.documentElement.hasAttribute("data-entry-pending")
      )
        return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener(
        "ts:scratch:revealed",
        () => this.unlock(),
        options,
      );
      this.querySelector(".ts-luxe-static-reveal")?.addEventListener(
        "toggle",
        (event) => {
          if (event.target.open) this.unlock();
        },
        options,
      );
    }
    unlock() {
      if (this.opening) return;
      this.opening = true;
      try {
        sessionStorage.setItem("ts:entry:unlocked", "1");
      } catch (error) {
        /* Storage is optional. */
      }
      this.querySelector("[data-entry-message]").textContent =
        this.dataset.welcome;
      const button = this.querySelector("[data-scratch-reveal]");
      if (button) button.disabled = true;
      this.revealTimer = setTimeout(() => {
        this.setAttribute("data-entry-leaving", "");
        this.exitTimer = setTimeout(
          () => {
            document.documentElement.removeAttribute("data-entry-pending");
            document.documentElement.setAttribute("data-entry-complete", "");
            const heading = document.querySelector(".ts-luxe-hero h1");
            if (heading) {
              heading.setAttribute("tabindex", "-1");
              heading.focus({ preventScroll: true });
            }
            window.scrollTo({ top: 0, behavior: "instant" });
          },
          matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 600,
        );
      }, 1100);
    }
    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.revealTimer);
      clearTimeout(this.exitTimer);
      if (!document.querySelector("[data-ts-entry-gate]"))
        document.documentElement.removeAttribute("data-entry-pending");
    }
  }
  class LuxeGallery extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector("[data-gallery-track]");
      this.slides = [...this.querySelectorAll("[data-gallery-slide]")];
      this.thumbs = [...this.querySelectorAll("[data-gallery-index]")];
      this.current = -1;
      if (!this.track || !this.slides.length) return;
      this.thumbs.forEach((button, index) => button.addEventListener("click", event => this.go(index, event.detail !== 0), options));
      this.querySelector("[data-gallery-prev]")?.addEventListener("click", event => this.go(this.current - 1, event.detail !== 0), options);
      this.querySelector("[data-gallery-next]")?.addEventListener("click", event => this.go(this.current + 1, event.detail !== 0), options);
      this.track.addEventListener("keydown", (event) => {
        if (event.target !== this.track) return;
        const destination = { ArrowLeft: this.current - 1, ArrowRight: this.current + 1, Home: 0, End: this.slides.length - 1 }[event.key];
        if (destination === undefined) return;
        event.preventDefault();
        this.go(destination, false);
      }, options);
      this.track.addEventListener("scroll", () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (this.track.clientWidth) this.update(Math.round(this.track.scrollLeft / this.track.clientWidth));
        });
      }, { ...options, passive: true });
      let previousWidth = this.track.clientWidth;
      this.resize = new ResizeObserver(() => {
        const width = this.track.clientWidth;
        if (!width || width === previousWidth) return;
        previousWidth = width;
        this.track.scrollTo({ left: Math.max(0, this.current) * width, behavior: "instant" });
      });
      this.resize.observe(this.track);
      this.update(0);
      this.setupDetails(options);
    }
    setupDetails(options) {
      const dialog = this.querySelector("[data-gallery-dialog]");
      if (!dialog || typeof dialog.showModal !== "function") return;
      const image = dialog.querySelector("[data-gallery-large]");
      const viewport = dialog.querySelector("[data-gallery-viewport]");
      const zoom = dialog.querySelector("[data-gallery-zoom]");
      const resetZoom = () => {
        dialog.removeAttribute("data-zoomed");
        zoom.setAttribute("aria-pressed", "false");
        zoom.textContent = zoom.dataset.zoomIn;
        viewport.scrollTo(0, 0);
      };
      this.querySelectorAll("[data-gallery-enlarge]").forEach(link => link.addEventListener("click", event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const slide = link.closest("[data-gallery-slide]") || this.slides[this.current];
        const source = slide?.querySelector("img");
        if (!source) return;
        resetZoom();
        image.alt = source.alt;
        image.onerror = () => { image.onerror = null; image.src = source.currentSrc || source.src; };
        image.src = link.href;
        dialog.querySelector("[data-gallery-large-caption]").textContent = source.alt;
        this.detailOpener = link;
        dialog.showModal();
        event.preventDefault();
      }, options));
      zoom.addEventListener("click", () => {
        if (dialog.hasAttribute("data-zoomed")) return resetZoom();
        image.style.setProperty("--ts-detail-width", `${Math.max(viewport.clientWidth * 2, image.naturalWidth)}px`);
        dialog.setAttribute("data-zoomed", "");
        zoom.setAttribute("aria-pressed", "true");
        zoom.textContent = zoom.dataset.zoomOut;
        viewport.scrollTo((viewport.scrollWidth - viewport.clientWidth) / 2, (viewport.scrollHeight - viewport.clientHeight) / 2);
      }, options);
      dialog.addEventListener("click", event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      }, options);
      dialog.addEventListener("close", () => {
        resetZoom();
        if (this.detailOpener?.isConnected) this.detailOpener.focus({ preventScroll: true });
      }, options);
    }
    go(index, animate = true) {
      const next = Math.max(0, Math.min(index, this.slides.length - 1));
      this.preload(next);
      this.track.scrollTo({ left: next * this.track.clientWidth, behavior: !animate || matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
    }
    selectMedia(id) {
      const index = this.slides.findIndex(slide => slide.dataset.mediaId === String(id));
      if (index >= 0) this.go(index, false);
    }
    preload(index) {
      this.slides[index]?.querySelectorAll("img").forEach(image => { image.loading = "eager"; });
    }
    update(index) {
      index = Math.max(0, Math.min(index, this.slides.length - 1));
      if (index === this.current) return;
      this.current = index;
      this.slides.forEach((slide, i) => { if (i !== index) slide.querySelectorAll("video").forEach(video => video.pause()); });
      this.thumbs.forEach((button, i) => button.setAttribute("aria-current", String(i === index)));
      const count = this.querySelector("[data-gallery-count]");
      if (count) count.textContent = `${index + 1} / ${this.slides.length}`;
      const previous = this.querySelector("[data-gallery-prev]");
      const next = this.querySelector("[data-gallery-next]");
      if (previous) previous.disabled = index === 0;
      if (next) next.disabled = index === this.slides.length - 1;
      const details = this.querySelector("[data-gallery-detail-control]");
      if (details) {
        const source = this.slides[index].querySelector("[data-gallery-enlarge]");
        details.hidden = !source;
        const hint = this.querySelector("[data-gallery-detail-hint]");
        if (hint) hint.hidden = Boolean(source);
        if (source) {
          details.href = source.href;
          details.setAttribute("aria-label", `${details.dataset.detailsLabel}: ${this.slides[index].querySelector("img").alt}`);
        }
      }
      this.preload(index);
      this.preload(index + 1);
      const thumb = this.thumbs[index];
      if (thumb) {
        const rail = thumb.parentElement;
        const left = thumb.offsetLeft - rail.offsetLeft;
        if (left < rail.scrollLeft || left + thumb.offsetWidth > rail.scrollLeft + rail.clientWidth)
          rail.scrollTo({ left: left - rail.clientWidth / 2 + thumb.offsetWidth / 2, behavior: "instant" });
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  }
  class LuxeProduct extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const gallery = this.querySelector("ts-luxe-gallery");
      const variants = JSON.parse(
        this.querySelector("[data-product-variants]").textContent,
      );
      const selector = this.querySelector("[data-variant-select]");
      const choices = [...this.querySelectorAll("[data-variant-choice]")];
      const form = this.querySelector(".ts-luxe-pdp-form");
      const addButton = this.querySelector("[data-add-button]");
      const label = this.querySelector("[data-add-label]");
      const status = this.querySelector(".ts-luxe-cart-message");
      const stickyButton = this.querySelector("[data-sticky-submit]");
      const stickyLabel = this.querySelector("[data-sticky-label]");
      const quantity = this.querySelector("[data-quantity]");
      let selected = variants.find(v => String(v.id) === form.elements.id.value);
      let adding = false;
      const updateButtons = () => {
        const disabled = adding || !selected?.available;
        addButton.disabled = disabled;
        if (stickyButton) stickyButton.disabled = disabled;
        const message = adding ? "Adding…" : selected?.available ? addButton.dataset.label : addButton.dataset.soldout;
        label.textContent = message;
        if (stickyLabel) stickyLabel.textContent = message;
        choices.forEach(button => {
          button.disabled = adding;
          button.setAttribute("aria-pressed", String(button.dataset.variantChoice === String(selected?.id)));
        });
      };
      const updateQuantity = () => {
        if (!quantity) return;
        const value = Number(quantity.value);
        this.querySelectorAll("[data-quantity-adjust]").forEach(button => {
          button.disabled = adding || (Number(button.dataset.quantityAdjust) < 0
            ? value <= Number(quantity.min)
            : Boolean(quantity.max) && value >= Number(quantity.max));
        });
        const each = this.querySelector("[data-price-each]");
        if (each) each.hidden = !(value > 1);
        const count = this.querySelector("[data-sticky-quantity]");
        if (count) {
          count.hidden = !(value > 1);
          count.textContent = ` × ${value}`;
        }
      };
      this.querySelectorAll("[data-quantity-adjust]").forEach(button => button.addEventListener("click", () => {
        if (Number(button.dataset.quantityAdjust) < 0) quantity.stepDown();
        else quantity.stepUp();
        quantity.dispatchEvent(new Event("input", { bubbles: true }));
      }, options));
      quantity?.addEventListener("input", updateQuantity, options);
      this.querySelector(".ts-luxe-purchase-promises a")?.addEventListener("click", event => {
        const id = event.currentTarget.hash.slice(1);
        const details = document.getElementById(id);
        if (details) details.open = true;
      }, options);
      const change = () => {
        const variant = variants.find((v) => String(v.id) === selector.value);
        if (!variant) return;
        selected = variant;
        this.querySelector("[data-product-price]").innerHTML = variant.price;
        if (quantity) {
          quantity.min = variant.quantityMin;
          quantity.step = variant.quantityStep;
          if (variant.quantityMax) quantity.max = variant.quantityMax;
          else quantity.removeAttribute("max");
          if (!quantity.validity.valid) quantity.value = variant.quantityMin;
          updateQuantity();
        }
        updateButtons();
        const url = new URL(location.href);
        url.searchParams.set("variant", variant.id);
        history.replaceState({}, "", url);
        if (variant.mediaId) gallery?.selectMedia(variant.mediaId);
        this.querySelector("[data-sticky-price]").innerHTML = variant.price;
        const variantLabel = this.querySelector("[data-sticky-variant]");
        if (variantLabel) variantLabel.textContent = variant.title;
        status.textContent = "";
      };
      selector?.addEventListener("change", change, options);
      choices.forEach(button => button.addEventListener("click", () => {
        if (!selector || adding) return;
        selector.value = button.dataset.variantChoice;
        selector.dispatchEvent(new Event("change", { bubbles: true }));
      }, options));
      updateButtons();
      updateQuantity();
      this.syncReviewSummary();
      window.addEventListener("pageshow", (event) => {
        if (!event.persisted) return;
        adding = false;
        if (selector) selector.disabled = false;
        if (quantity) quantity.readOnly = false;
        selected = variants.find(v => String(v.id) === form.elements.id.value);
        updateButtons();
        updateQuantity();
        addButton.removeAttribute("aria-busy");
        stickyButton?.removeAttribute("aria-busy");
        status.textContent = "";
      }, options);
      const sticky = this.querySelector("[data-sticky-purchase]");
      if (sticky && "IntersectionObserver" in window) {
        this.purchaseObserver = new IntersectionObserver(([entry]) => { sticky.hidden = entry.intersectionRatio >= 0.99; }, { threshold: 1 });
        this.purchaseObserver.observe(addButton);
      }

      form.addEventListener(
        "submit",
        async (event) => {
          event.preventDefault();
          if (adding || !selected?.available || !form.reportValidity()) return;
          const data = new FormData(form);
          let completed = false;
          adding = true;
          updateButtons();
          updateQuantity();
          if (selector) selector.disabled = true;
          if (quantity) quantity.readOnly = true;
          addButton.setAttribute("aria-busy", "true");
          stickyButton?.setAttribute("aria-busy", "true");
          status.textContent = "";
          try {
            const response = await fetch(this.dataset.addUrl, {
              method: "POST",
              body: data,
              headers: { Accept: "application/json" },
              signal: this.controller.signal,
            });
            const result = await response.json().catch(() => null);
            if (!response.ok || !result)
              throw new Error(
                (typeof result?.description === "string" && result.description) ||
                  (response.status === 429
                    ? "Please wait a moment, then try adding this game again."
                    : "We couldn’t add this game right now. Please try again."),
              );
            status.textContent = "Added to your bag. Opening your bag…";
            completed = true;
            location.assign(this.dataset.cartUrl);
          } catch (error) {
            if (error.name === "AbortError") return;
            status.textContent = error.message || "Please try again.";
            status.focus();
          } finally {
            if (!completed) {
              adding = false;
              if (selector) selector.disabled = false;
              if (quantity) quantity.readOnly = false;
              updateButtons();
              updateQuantity();
              addButton.removeAttribute("aria-busy");
              stickyButton?.removeAttribute("aria-busy");
            }
          }
        },
        options,
      );
    }
    syncReviewSummary() {
      const target = document.getElementById('bundle-product_reviews');
      const link = this.querySelector('.ts-luxe-rating-fallback');
      if (!target || !link?.querySelector('[data-review-count]')) return;
      const update = () => {
        const score = Number(target.querySelector('.vtl-pr-totals__score')?.textContent.trim());
        const count = Number(target.querySelector('.vtl-pr-totals__reviews span')?.textContent.replace(/[^0-9]/g, ''));
        if (!(score > 0 && score <= 5 && count > 0)) return;
        const key = `${score}:${count}`;
        if (link.dataset.reviewSource === key) return;
        link.dataset.reviewSource = key;
        link.href = '#bundle-product_reviews';
        link.setAttribute('aria-label', `${score} out of 5 from ${count} product reviews`);
        link.querySelector('[data-review-score]').textContent = `${score}/5`;
        link.querySelector('[data-review-count]').textContent = `${count.toLocaleString()} reviews`;
        link.querySelector('.ts-luxe-rating-stars').style.setProperty('--ts-rating-fill', `${score * 20}%`);
      };
      this.reviewObserver = new MutationObserver(() => {
        cancelAnimationFrame(this.reviewFrame);
        this.reviewFrame = requestAnimationFrame(update);
      });
      this.reviewObserver.observe(target, { childList: true, subtree: true, characterData: true });
      update();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.purchaseObserver?.disconnect();
      this.reviewObserver?.disconnect();
      cancelAnimationFrame(this.reviewFrame);
    }
  }
  customElements.define("ts-luxe-menu", LuxeMenu);
  customElements.define("ts-luxe-scratch", LuxeScratch);
  customElements.define("ts-luxe-entry", LuxeEntry);
  customElements.define("ts-luxe-gallery", LuxeGallery);
  customElements.define("ts-luxe-product", LuxeProduct);
  const reduced = matchMedia("(prefers-reduced-motion: reduce)");
  let observer;
  const initMotion = (root = document) => {
    if (reduced.matches || !("IntersectionObserver" in window)) return;
    document.documentElement.classList.add("ts-luxe-motion");
    observer ||= new IntersectionObserver(
      (entries) =>
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) {
            target.classList.add("ts-luxe-seen");
            observer.unobserve(target);
          }
        }),
      { threshold: 0.08 },
    );
    root.querySelectorAll("[data-reveal]").forEach((node) => {
      if (node.getBoundingClientRect().top > innerHeight) {
        node.classList.add("ts-luxe-await");
        observer.observe(node);
      }
    });
  };
  initMotion();

  // The collection is a buying section. On a wide screen its editorial heading
  // is tall enough to push the product row below the fold when the native anchor
  // lands at the section top, so guide same-page links to the row itself. Links
  // that start on another page keep their normal navigation behavior.
  const collectionLinks = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.hash !== '#collection') return;
    const products = document.querySelector('#collection .ts-luxe-products');
    if (!products) return;
    event.preventDefault();
    const header = document.querySelector('.ts-luxe-header-section, .ts-luxe-nav')?.getBoundingClientRect();
    const offset = (header?.height || 0) + 18;
    const top = products.getBoundingClientRect().top + scrollY - offset;
    history.pushState(null, '', `${url.pathname}${url.search}#collection`);
    scrollTo({ top: Math.max(0, top), behavior: reduced.matches ? 'auto' : 'smooth' });
  };
  document.addEventListener('click', collectionLinks);

  document.addEventListener("shopify:section:load", (event) =>
    initMotion(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) =>
    event.target
      .querySelectorAll("[data-reveal]")
      .forEach((node) => observer?.unobserve(node)),
  );
  reduced.addEventListener("change", () => {
    document.documentElement.classList.toggle(
      "ts-luxe-motion",
      !reduced.matches,
    );
    if (reduced.matches) observer?.disconnect();
    else initMotion();
  });
  let frame = 0;
  addEventListener(
    "scroll",
    () => {
      if (frame || reduced.matches || innerWidth < 750) return;
      frame = requestAnimationFrame(() => {
        const hero = document.querySelector(".ts-luxe-hero");
        if (hero) {
          const bounds = hero.getBoundingClientRect();
          if (bounds.bottom > 0)
            hero.style.setProperty(
              "--ts-parallax",
              Math.min(35, Math.max(0, -bounds.top * 0.07)) + "px",
            );
        }
        frame = 0;
      });
    },
    { passive: true },
  );
})();
